<template>
  <div class="c-frame-body p-entry">
    <div class="c-frame-logo">
      <img src="@/styles/img/login/img_logo.svg" alt="MANAVIRIA">
    </div>
    <h1 class="c-frame-heading -big">マナビリア教材お申し込みフォーム</h1>
    <div class="c-frame-inner -wide">
      <entry-step :step="step" :isLogin="isTeacher" />
      <div class="p-entry-info">
        <p class="c-frame-description">
          この度は、明治図書出版のデジタル教材「マナビリア」にお申し込みいただき、ありがとうございます。学校・先生情報と教材情報のご入力をお願いいたします。
        </p>
        <div class="c-markdown-area">
          <markdown-view-teacher :rawText="rawText"></markdown-view-teacher>
        </div>
      </div>
      <form
        action=""
        class="p-entry-form"
        @submit.stop.prevent
      >
        <div id="error" class="c-form-error-area" v-if="formError">
          <p class="c-form-error-title">{{ formError.title }}</p>
          <p class="c-form-error-announce" v-html="formError.text"></p>
        </div>
        <p class="p-entry-title" id="step1">
          <template v-if="isTeacher">【STEP1】ご連絡先電話番号をご入力ください</template>
          <template v-else>【STEP1】学校・先生情報を入力してください</template>
        </p>
        <template v-if="!isTeacher">
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">学校種別</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <p class="p-entry-title-form__text">
                学校の種別を選択してください
              </p>
              <div class="c-form-radio-area">
                <label 
                  v-for="option in schoolTypeCodeOptions"
                  class="c-form-radio" 
                  :for="`school_type_code${option.value}`" 
                  :key="option.value"
                >
                  <input 
                    type="radio" 
                    name="school_type_code" 
                    :value="option.value"
                    :id="`school_type_code${option.value}`" 
                    class="c-form-radio__input"
                    @change="changeSchoolTypeCode(option)"
                  >
                  <span class="c-form-radio__label">{{ option.label }}</span>
                </label>
              </div>
              <p class="c-form-reference">義務教育学校、小中一貫校の前期課程（１～６年）は「小学校」を、後期課程（７～９年）は「中学校」を選択してください。</p>
            </dd>
          </dl>
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">{{ title.schoolName }}</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <p class="p-entry-title-form__text">
                所属校を選択してください。学校名の一部を入力すると、候補の学校が表示されます。
              </p>
              <div class="c-form-area">
                <div class="c-form-textbox" :class="{ '-error': error.schoolName }">
                  <input
                    type="text"
                    list="schools"
                    name="school_name"
                    id="school_name"
                    v-model.lazy.trim="form.schoolName"
                    class="c-form-textbox__input"
                    placeholder="所属校を選択"
                    required
                    data-required-step1="true"
                    @blur="validateSchool('schoolName')"
                  />
                  <datalist id="schools">
                    <option
                      v-for="school in schools"
                      :key="school.code"
                      :value="school.name + '（' + school.prefecture_name + '）'"
                    >
                    </option>
                  </datalist>
                </div>
              </div>
              <p class="c-form-error-text" v-show="error.schoolName">{{ error.schoolName }}</p>
              <p class="c-form-reference">
                所属校が表示されない場合は先に学校の登録が必要となりますので、<a
                  href="https://www.meijitosho.co.jp/gakusan/manaviria/contact/"
                  target="_blank"
                  class="c-parts-link"
                  >こちら</a
                >
                からお問い合わせください。
              </p>
              <p class="c-form-reference">
                日本人学校等、海外に所在する学校の場合は <a
                  href="https://www.meijitosho.co.jp/gakusan/manaviria/contact/"
                  target="_blank"
                  class="c-parts-link"
                  >こちら</a
                >
                からお問い合わせください。
              </p>
            </dd>
          </dl>
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">{{ title.phone }}</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <p class="p-entry-title-form__text">
                市外局番から半角数字でご入力ください。（ハイフン、かっこなし）
              </p>
              <div class="c-form-area-small">
                <div class="c-form-textbox" :class="{ '-error': error.phone }">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    v-model.lazy.trim="form.phone"
                    class="c-form-textbox__input"
                    placeholder="例) 03012345678"
                    required
                    data-required-step1="true"
                    @blur="validatePhone('phone')"
                    maxlength="16"
                  />
                </div>
              </div>
              <p class="c-form-error-text" v-show="error.phone">{{ error.phone }}</p>
            </dd>
          </dl>
        </template>
        <dl class="p-entry-title-form">
          <dt class="p-entry-title-form__subtitle">
            <span class="title">{{ title.contactPhone }}</span>
            <span class="c-parts-input-type -required">必須</span>
          </dt>
          <dd class="p-entry-title-form__contents">
            <p class="p-entry-title-form__text">
              市外局番から半角数字でご入力ください。（ハイフン、かっこなし）
            </p>
            <div class="c-form-area-small">
              <div class="c-form-textbox" :class="{ '-error': error.contactPhone }">
                <input
                  type="tel"
                  v-model.lazy.trim="form.contactPhone"
                  id="contactphone"
                  class="c-form-textbox__input"
                  placeholder="例) 03012345678"
                  required
                  data-required-step1="true"
                  @blur="validatePhone('contactPhone')"
                  maxlength="16"
                />
              </div>
            </div>
            <p class="c-form-error-text" v-show="error.contactPhone">{{ error.contactPhone }}</p>
            <p class="c-form-reference">平日日中に連絡のとれる番号をご記入ください。</p>
          </dd>
        </dl>
        <template v-if="!isTeacher">
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">{{ title.applicantName }}</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <div class="c-form-area-half">
                <div class="c-form-textbox" :class="{ '-error': error.applicantName }">
                  <input
                    type="text"
                    name="applicant_name"
                    id="applicant_name"
                    v-model.lazy.trim="form.applicantName"
                    class="c-form-textbox__input"
                    placeholder="例) 山田　太郎"
                    required
                    data-required-step1="true"
                    @blur="validateText('applicantName')"
                    maxlength="255"
                  />
                </div>
              </div>
              <p class="c-form-error-text" v-show="error.applicantName">{{ error.applicantName }}</p>
              <p class="c-form-reference">ご入力いただいたお名前がマナビリアに登録されます。（登録後に変更可能です）</p>
            </dd>
          </dl>
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">{{ title.email }}</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <div class="c-form-area-half">
                <div class="c-form-textbox" :class="{ '-error': error.email }">
                  <input
                    type="email"
                    v-model.lazy.trim="form.email"
                    name="email"
                    id="email"
                    class="c-form-textbox__input"
                    placeholder="例) t-yamada@example.com"
                    required
                    data-required-step1="true"
                    @blur="validateEmail('email')"
                    maxlength="254"
                  />
                </div>
              </div>
              <p class="c-form-error-text" v-show="error.email">{{ error.email }}</p>
              <p class="c-form-reference">マナビリア利用開始時に必要なURLなどをお送りいたします。</p>
              <p class="c-form-reference">ご案内は「no-reply@meijitosho.co.jp」のメールアドレスから送信いたします。</p>
              <p class="c-form-reference">迷惑メール対策を行っている場合「@meijitosho.co.jp」からのメールを受信できるよう設定をお願いします。</p>
              <p class="c-form-reference">バージョンアップのお知らせや機能改善のためのアンケートをお送りする場合もございます。</p>
              <p class="c-form-reference">アドレスの変更やメールの配信停止はいつでも可能です。</p>
            </dd>
          </dl>
        </template>
        <entry-table
          v-if="isTeacher"
          :schoolName="teacherInfo.schoolName"
          :schoolPhone="teacherInfo.schoolPhone"
          :name="teacherInfo.applicantName"
          :email="teacherInfo.email"
        ></entry-table>
        <div class="c-frame-button -large-margin" v-if="!isStep1Submitted">
          <button
            type="button"
            class="c-button-blue -small-no-icon"
            id="next1"
            :class="{ '-disabled': isNextButtonDisabled }"
            :disabled="isNextButtonDisabled"
            @click="nextStep"
          >
            <span class="label">次へ（教材を選択）</span>
          </button>
        </div>

        <!-- ここからSTEP2 -->
        <div class="p-entry-nextform" id="step2form" :class="{'-display': isStep1Submitted}">
          <p class="p-entry-title -top" id="step2">【STEP2】教材を選択してください。</p>
          <dl class="p-entry-title-form">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">ご利用開始年度</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <!-- 年度選択 -->
            <dd class="p-entry-title-form__contents">
              <div class="c-form-radio-area">
                <label
                  v-for="latestAcademicSession in latestAcademicSessionResult"
                  :key="latestAcademicSession.id"
                  class="c-form-radio"
                  :for="`${latestAcademicSession.id}`"
                >
                  <input
                    type="radio"
                    name="academic_session_id"
                    :value="latestAcademicSession.year"
                    :id="`${latestAcademicSession.id}`"
                    class="c-form-radio__input"
                    checked
                  >
                  <span class="c-form-radio__label">{{ latestAcademicSession.year }}年度</span>
                </label>
              </div>
              <p class="c-form-reference">選択肢にない年度の教材はお申し込みいただくことができません。</p>
            </dd>
          </dl>

          <dl class="p-entry-title-form" v-if="form.schoolTypeCode===SCHOOL_TYPE_CODE.JUNIOR_HIGH_SCHOOL">
            <dt class="p-entry-title-form__subtitle">
              <span class="title">教材の種類</span>
              <span class="c-parts-input-type -required">必須</span>
            </dt>
            <dd class="p-entry-title-form__contents">
              <p class="p-entry-title-form__text">
                お申し込みいただく教材の種類を選択してください
              </p>
              <div class="c-form-radio-area">
                <label 
                  v-for="option in licenseTypeOptions"
                  class="c-form-radio" 
                  :for="`license_type_id${option.value}`" 
                  :key="option.value"
                >
                  <input 
                    type="radio" 
                    name="license_type_id" 
                    :value="option.value"
                    :id="`license_type_id${option.value}`" 
                    class="c-form-radio__input"
                    @change="changeLicenseType(option)"
                  >
                  <span class="c-form-radio__label">{{ option.label }}</span>
                </label>
              </div>
            </dd>
          </dl>
          <template v-if="selectedLicenseType">
            <!-- 教科選択 -->
            <dl class="p-entry-title-form">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">「{{ selectedLicenseType.label }}」の教科</span>
                <span class="c-parts-input-type -required">必須</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text">マナビリアの「{{ selectedLicenseType.label }}」でお申込みされる教材をお選びください。</p>
                <table class="p-entry-subject" :class="{ '-required': hasInteractedSubjectField && !isSubjectFieldChecked }">
                  <tbody>
                    <!-- Elementary school (CBT) -->
                    <template v-if="form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL">
                      <!-- 3年生 -->
                      <tr>
                        <th>3年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all3">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all3"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_3)"
                                  :checked="isAllSubjectFieldsChecked.elementary.workOrCbt.grade3"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.elementary.workOrCbt.grade3"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 3"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 3"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_3, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 4年生 -->
                      <tr>
                        <th>4年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all4">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all4"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_4)"
                                  :checked="isAllSubjectFieldsChecked.elementary.workOrCbt.grade4"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.elementary.workOrCbt.grade4To6"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 4"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 4"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_4]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_4, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 5年生 -->
                      <tr>
                        <th>5年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all5">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all5"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_5)"
                                  :checked="isAllSubjectFieldsChecked.elementary.workOrCbt.grade5"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.elementary.workOrCbt.grade4To6"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 5"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 5"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_5]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_5, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 6年生 -->
                      <tr>
                        <th>6年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all6">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all6"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_6)"
                                  :checked="isAllSubjectFieldsChecked.elementary.workOrCbt.grade6"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.elementary.workOrCbt.grade4To6"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 6"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 6"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_6]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_6, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <!-- Junior high school (WORK or CBT)-->
                    <template v-else-if="selectedLicenseType.value === LICENSE_TYPE.WORK || selectedLicenseType.value === LICENSE_TYPE.CBT">
                      <!-- 1年生 -->
                      <tr>
                        <th>1年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all1">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all1"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_1)"
                                  :checked="isAllSubjectFieldsChecked.juniorHigh.workOrCbt.grade1"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.juniorHigh.workOrCbt.grade1To2"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 1"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 1"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_1]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_1, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 2年生 -->
                      <tr>
                        <th>2年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all2">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all2"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_2)"
                                  :checked="isAllSubjectFieldsChecked.juniorHigh.workOrCbt.grade2"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.juniorHigh.workOrCbt.grade1To2"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 2"
                              >
                                <input
                                type="checkbox"
                                class="c-form-checkbox"
                                :id="subjectField.name + 2"
                                :value="subjectField.val"
                                v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_2]"
                                @change="changeSubjectField(ACADEMIC_YEAR.GRADE_2, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 3年生 -->
                      <tr>
                        <th>3年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all3">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all3"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_3)"
                                  :checked="isAllSubjectFieldsChecked.juniorHigh.workOrCbt.grade3"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.juniorHigh.workOrCbt.grade3"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :class="{ 'p-entry-subject-right': subjectField.name === 'komin' }"
                                :for="subjectField.name + 3"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 3"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_3, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <!-- Junior high school (NYUSHI)-->
                    <template v-else-if="selectedLicenseType.value === LICENSE_TYPE.NYUSHI">
                      <!-- 2年生 -->
                      <tr>
                        <th>2年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all2">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all2"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_2)"
                                  :checked="isAllSubjectFieldsChecked.juniorHigh.nyushi.grade2"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.juniorHigh.nyushi.grade2To3"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 2"
                              >
                                <input
                                type="checkbox"
                                class="c-form-checkbox"
                                :id="subjectField.name + 2"
                                :value="subjectField.val"
                                v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_2]"
                                @change="changeSubjectField(ACADEMIC_YEAR.GRADE_2, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <!-- 3年生 -->
                      <tr>
                        <th>3年生</th>
                        <td>
                          <div class="p-entry-subject-class-wrapper">
                            <div class="p-entry-subject-class">
                              <label class="c-form-checkbox-area" for="all3">
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  id="all3"
                                  @change="changeAllSubjectFields(ACADEMIC_YEAR.GRADE_3)"
                                  :checked="isAllSubjectFieldsChecked.juniorHigh.nyushi.grade3"
                                >
                                <span class="c-form-checkbox-label -large">全教科</span>
                              </label>
                            </div>
                            <div class="p-entry-subject-class">
                              <label
                                v-for="subjectField in subjectFields.juniorHigh.nyushi.grade2To3"
                                :key="subjectField.val"
                                class="c-form-checkbox-area"
                                :for="subjectField.name + 3"
                              >
                                <input
                                  type="checkbox"
                                  class="c-form-checkbox"
                                  :id="subjectField.name + 3"
                                  :value="subjectField.val"
                                  v-model="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3]"
                                  @change="changeSubjectField(ACADEMIC_YEAR.GRADE_3, subjectField)"
                                >
                                <span class="c-form-checkbox-label -large">{{ subjectField.label }}</span>
                              </label>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
                <p class="c-form-error-text" v-show="hasInteractedSubjectField && !isSubjectFieldChecked">
                  {{ errorMessages.subjectFieldRequired }}
                </p>
                <div>
                  <p class="c-form-reference">本誌記載の学年と異なるご利用方法の場合は <a href="https://www.meijitosho.co.jp/gakusan/manaviria/contact/" target="_blank" class="c-parts-link">こちら</a> からご連絡ください。別途ご案内いたします。</p>
                </div>
              </dd>
            </dl>

            <!-- 教科書会社選択 -->
            <!-- 中学校の「入試対策版」のときは表示しない（「標準」が選択されているものとして扱う） -->
            <dl class="p-entry-title-form" v-if="selectedLicenseType.value !== LICENSE_TYPE.NYUSHI">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">「{{ selectedLicenseType.label }}」の教科書会社名</span>
                <span class="c-parts-input-type -required">必須</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text">
                  マナビリアをお申し込みされる「{{ selectedLicenseType.label }}」の教科書会社名を確認してください。
                </p>
                <!-- Elementary school (CBT) -->
                <template v-if="form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL">
                  <!-- 3年生 -->
                  <template v-for="subject in subjectFields.elementary.workOrCbt.grade3" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">3年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_3)"
                          v-model:selected="selectedTextbookPublisher.elementary.workOrCbt.grade3[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.elementary.workOrCbt.grade3[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                  <!-- 4年生 -->
                  <template v-for="subject in subjectFields.elementary.workOrCbt.grade4To6" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_4].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">4年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_4)"
                          v-model:selected="selectedTextbookPublisher.elementary.workOrCbt.grade4[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.elementary.workOrCbt.grade4[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                  <!-- 5年生 -->
                  <template v-for="subject in subjectFields.elementary.workOrCbt.grade4To6" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_5].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">5年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_5)"
                          v-model:selected="selectedTextbookPublisher.elementary.workOrCbt.grade5[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.elementary.workOrCbt.grade5[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                  <!-- 6年生 -->
                  <template v-for="subject in subjectFields.elementary.workOrCbt.grade4To6" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_6].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">6年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_6)"
                          v-model:selected="selectedTextbookPublisher.elementary.workOrCbt.grade6[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.elementary.workOrCbt.grade6[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                </template>
                <!-- Junior high school (WORK or CBT) -->
                <template v-else>
                  <!-- 1年生 -->
                  <template v-for="subject in subjectFields.juniorHigh.workOrCbt.grade1To2" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_1].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">1年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_1)"
                          v-model:selected="selectedTextbookPublisher.juniorHigh.workOrCbt.grade1[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.juniorHigh.workOrCbt.grade1[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                  <!-- 2年生 -->
                  <template v-for="subject in subjectFields.juniorHigh.workOrCbt.grade1To2" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_2].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">2年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_2)"
                          v-model:selected="selectedTextbookPublisher.juniorHigh.workOrCbt.grade2[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.juniorHigh.workOrCbt.grade2[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                  <!-- 3年生 -->
                  <template v-for="subject in subjectFields.juniorHigh.workOrCbt.grade3" :key="subject.val">
                    <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3].includes(subject.val)">
                      <div class="p-entry-textbook">
                        <p class="p-entry-textbook-year">3年生 {{ subject.label }}</p>
                        <select-single
                          :options="getTextbookPublisherOptions(subject.val, ACADEMIC_YEAR.GRADE_3)"
                          v-model:selected="selectedTextbookPublisher.juniorHigh.workOrCbt.grade3[subject.name]"
                          :init-blank="true"
                        ></select-single>
                        <p class="c-form-error-text" v-show="!selectedTextbookPublisher.juniorHigh.workOrCbt.grade3[subject.name]">
                          {{ errorMessages.textbookPublisherRequired }}
                        </p>
                      </div>
                    </template>
                  </template>
                </template>
                <p class="c-form-error-text" v-show="hasInteractedSubjectField && !isSubjectFieldChecked">
                  {{ errorMessages.subjectFieldRequired + errorMessages.subjectFieldRequiredForTextbookPublisher }}
                </p>
              </dd>
            </dl>

            <!-- 生徒数 -->
            <dl class="p-entry-title-form">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">利用学年の人数</span>
                <span class="c-parts-input-type -required">必須</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text">
                  各学年の生徒数をご入力ください。
                </p>
                <div class="p-entry-year">
                  <!-- 1年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_1].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade1_students">1年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade1 }">
                        <input
                          type="number"
                          name="number_of_grade1_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade1_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_1]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_1)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade1">
                            {{ error.numOfStudentsGrade1 }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <!-- 2年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_2].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade2_students">2年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade2 }">
                        <input
                          type="number"
                          name="number_of_grade2_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade2_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_2]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_2)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade2">
                          {{ error.numOfStudentsGrade2 }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <!-- 3年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_3].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade3_students">3年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade3 }">
                        <input
                          type="number"
                          name="number_of_grade3_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade3_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_3]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_3)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade3">
                          {{ error.numOfStudentsGrade3 }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <!-- 4年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_4].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade4_students">4年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade4 }">
                        <input
                          type="number"
                          name="number_of_grade4_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade4_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_4]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_4)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade4">
                            {{ error.numOfStudentsGrade4 }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <!-- 5年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_5].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade5_students">5年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade5 }">
                        <input
                          type="number"
                          name="number_of_grade5_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade5_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_5]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_5)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade5">
                            {{ error.numOfStudentsGrade5 }}
                        </p>
                      </div>
                    </div>
                  </template>
                  <!-- 6年生 -->
                  <template v-if="checkedSubjectFields[ACADEMIC_YEAR.GRADE_6].length">
                    <div class="p-entry-year-contents">
                      <label class="p-entry-year-label" for="number_of_grade6_students">6年生</label>
                      <div class="c-form-textbox -year" :class="{ '-error': error.numOfStudentsGrade6 }">
                        <input
                          type="number"
                          name="number_of_grade6_students"
                          value=""
                          min="1"
                          max="999"
                          id="number_of_grade6_students"
                          class="c-form-textbox__input"
                          placeholder="例) 120"
                          required
                          data-required-step2="true"
                          v-model="numOfStudents[ACADEMIC_YEAR.GRADE_6]"
                          @blur="validateNumOfStudents(ACADEMIC_YEAR.GRADE_6)"
                        >
                        <p class="c-form-error-text -small" v-show="error.numOfStudentsGrade6">
                            {{ error.numOfStudentsGrade6 }}
                        </p>
                      </div>
                    </div>
                  </template>
                </div>
                <p class="c-form-error-text" v-show="hasInteractedSubjectField && !isSubjectFieldChecked">
                  {{ errorMessages.subjectFieldRequired + errorMessages.subjectFieldRequiredForStudent }}
                </p>
              </dd>
            </dl>

            <dl class="p-entry-title-form">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">前年度もマナビリアをご利用していた先生へ</span>
                <span class="c-parts-input-type -mandatory">任意</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text -margin">
                  前年度に使用していた生徒のID・パスワード・教材を次年度も引き継ぐ場合、既存の生徒アカウントに対して「進級処理」を行って頂くことが必須です。<br>
                  進級処理をせずに生徒アカウントを新しく登録し直した場合、<strong>現在の生徒のIDやパスワードは全て変更され、前年度に使用していた教材は使えなくなります。</strong><br>
                  新年度の教材で学習を開始する前までに、必ず先生用画面へログインし進級処理を実施してください。<br>
                  ご確認いただけた方は下記にチェックをお願いいたします。
                </p>
                <label class="c-form-checkbox-area" for="grade-check">
                  <input type="checkbox" class="c-form-checkbox" id="grade-check">
                  <span class="c-form-checkbox-label -large">確認した</span>
                </label>
              </dd>
            </dl>
            <dl class="p-entry-title-form">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">個人情報の取り扱いについて</span>
                <span class="c-parts-input-type -required">必須</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text -margin">
                  ご記入いただく個人情報は明治図書出版株式会社が定める個人情報の取り扱い規程に沿って適切に情報を取り扱います。<br>
                  このフォームから送信された情報はお問い合わせの回答以外には使用いたしません。<br>
                  詳細は弊マナビリアの「<a href="https://www.meijitosho.co.jp/gakusan/manaviria/help/teacher/" target="_blank" class="c-parts-link u-text-bold">サポートページ</a>」に掲載している「セキュリティポリシー」や「個人情報の取り扱い」をご参照ください。
                </p>
                <label class="c-form-checkbox-area" for="agree">
                  <input
                    type="checkbox"
                    class="c-form-checkbox"
                    id="agree"
                    v-model="personalInfoConsent.agree"
                    data-required-step2="true"
                    @change="validateAgree"
                  >
                  <span class="c-form-checkbox-label -large">個人情報の取り扱いに同意する</span>
                </label>
                <p class="c-form-error-text" v-show="error.agree">{{ error.agree }}</p>
              </dd>
            </dl>
            <dl class="p-entry-title-form">
              <dt class="p-entry-title-form__subtitle">
                <span class="title">免責事項</span>
                <span class="c-parts-input-type -required">必須</span>
              </dt>
              <dd class="p-entry-title-form__contents">
                <p class="p-entry-title-form__text -margin">
                  教科や準拠版によっては取り組める問題を順次公開とさせていただいております。<br>
                  また一部の問題形式および準拠版はマナビリアに対応しておりません。<br>
                  公開スケジュールや対応状況の詳細はお申し込みの前にサポートページにて必ずご確認ください。<br>
                  ⇒ <a href="https://s.meijitosho.co.jp/84fj33h" target="_blank" class="c-parts-link u-text-bold">公開スケジュール・対応状況はこちら</a>
                </p>
                <label class="c-form-checkbox-area" for="disclaimer">
                  <input
                    type="checkbox"
                    class="c-form-checkbox"
                    id="disclaimer"
                    v-model="personalInfoConsent.disclaimer"
                    data-required-step2="true"
                    @change="validateDisclaimer"
                  >
                  <span class="c-form-checkbox-label -large">内容を確認し同意する。</span>
                </label>
                <p class="c-form-error-text" v-show="error.disclaimer">{{ error.disclaimer }}</p>
              </dd>
            </dl>
            <div class="c-frame-button -large-margin">
              <div class="c-button -col2 p-entry-button">
                <a class="c-button-white -cancel" href="#step1">
                  <span class="label">STEP1に戻る</span>
                </a>
                <button
                  type="submit"
                  class="c-button-blue -small-no-icon -type02"
                  :class="{ '-disabled': isEntryButtonDisabled }"
                  id="next2"
                  :disabled="isEntryButtonDisabled"
                  @click="addEntry"
                >
                  <span class="label">入力内容で申し込む</span>
                </button>
              </div>
            </div>
          </template>
        </div>
      </form>
    </div>
  </div>
  <error-modal></error-modal>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, reactive, nextTick, watch } from "vue";
  import { useEntryFormStore } from "@/store/teacher/entry_form_store";
  import {
    ILatestAcademicSessionResult,
    IEntrySchool,
    IEntry,
    ITeacherError,
    ISelectSingleOption,
    ITextbookPublishersResult,
    IPersonalInfoConsent,
    IEntryTeacherInfo,
  } from "@/consts/interfaces";

  const props = defineProps<{
    rawText: string;
    isTeacher: boolean;
    teacherInfo: IEntryTeacherInfo;
  }>();

  const entryAddResult = ref<any>(null);
  const entryStore = useEntryFormStore();
  const formError = ref<ITeacherError | null>(null);
  const latestAcademicSessionResult = ref<Array<ILatestAcademicSessionResult>>([]);
  const textbookPublishersResultList = ref<Array<ITextbookPublishersResult>>([]);

  interface IEntryFormSelectSingleOption {
    value: number;
    label: string;
  }

  // NFR-6629: license type radio button selection
  const LICENSE_TYPE = {
    WORK: 1,
    CBT: 2,
    NYUSHI: 3,
  }
  const selectedLicenseType = ref<IEntryFormSelectSingleOption | null>(null);
  const licenseTypeOptions: IEntryFormSelectSingleOption[] = [
    { value: LICENSE_TYPE.CBT, label: 'CBT' },
    { value: LICENSE_TYPE.WORK, label: 'ワーク' },
    { value: LICENSE_TYPE.NYUSHI, label: '入試対策版' }
  ];

  // NFR-6643: schoolType radio button selection
  const SCHOOL_TYPE_CODE = {
    ELEMENTARY_SCHOOL: 1,
    JUNIOR_HIGH_SCHOOL: 2,
  }
  const schoolTypeCodeOptions: IEntryFormSelectSingleOption[] = [
    { value: SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL, label: '小学校' },
    { value: SCHOOL_TYPE_CODE.JUNIOR_HIGH_SCHOOL, label: '中学校' }
  ];
  
  // NFR-6630: make a academic year const
  const ACADEMIC_YEAR = {
    GRADE_1: 1,
    GRADE_2: 2,
    GRADE_3: 3,
    GRADE_4: 4,
    GRADE_5: 5,
    GRADE_6: 6,
  }

  // 教科チェックボックス用
  const subjectFields = {
    juniorHigh: {
      workOrCbt: {
        grade1To2: [
          { val: 1, label: "国語", name: "kokugo" },
          { val: 2, label: "地理", name: "chiri" },
          { val: 3, label: "歴史", name: "rekishi" },
          { val: 5, label: "数学", name: "sugaku" },
          { val: 6, label: "理科", name: "rika" },
          { val: 7, label: "英語", name: "eigo" },
        ],
        grade3: [
          { val: 1, label: "国語", name: "kokugo" },
          { val: 4, label: "公民", name: "komin" },
          { val: 5, label: "数学", name: "sugaku" },
          { val: 6, label: "理科", name: "rika" },
          { val: 7, label: "英語", name: "eigo" },
        ],
      },
      nyushi: {
        grade2To3: [
          { val: 1, label: "国語", name: "kokugo" },
          { val: 13, label: "社会", name: "shakai" },
          { val: 5, label: "数学", name: "sugaku" },
          { val: 6, label: "理科", name: "rika" },
          { val: 7, label: "英語", name: "eigo" },
        ]
      }
    },
    elementary: {
      workOrCbt: {
        grade3: [
          { val: 10, label: "理科", name: "rika" },
        ],
        grade4To6: [
          { val: 11, label: "社会", name: "shakai" },
          { val: 10, label: "理科", name: "rika" },
        ],
      }
    }
  };
  // 教科分野idの配列
  const subjectFieldsVals = {
    juniorHigh: {
      workOrCbt: {
        grade1To2: subjectFields.juniorHigh.workOrCbt.grade1To2.map(subjectField => subjectField.val),
        grade3: subjectFields.juniorHigh.workOrCbt.grade3.map(subjectField => subjectField.val),
      },
      nyushi: {
        grade2To3: subjectFields.juniorHigh.nyushi.grade2To3.map(subjectField => subjectField.val)
      }
    },
    elementary: {
      workOrCbt: {
        grade3: subjectFields.elementary.workOrCbt.grade3.map(subjectField => subjectField.val),
        grade4To6: subjectFields.elementary.workOrCbt.grade4To6.map(subjectField => subjectField.val),
      }
    }
  };

  // ユーザーが教科チェックボックスを一度でも操作したかどうか
  // - 初期表示のときは errorMessages.subjectFieldRequired のエラーメッセージが表示されないようにするためのフラグ
  // - 教科チェックボックスの上位項目である「教材の種類」が選択し直されたときは、再び false にリセットする
  const hasInteractedSubjectField = ref(false);
  // 各学年の教科チェックボックスの選択状態を保持する配列
  const checkedSubjectFields = ref<Record<number, number[]>>({
    [ACADEMIC_YEAR.GRADE_1]: [],
    [ACADEMIC_YEAR.GRADE_2]: [],
    [ACADEMIC_YEAR.GRADE_3]: [],
    [ACADEMIC_YEAR.GRADE_4]: [],
    [ACADEMIC_YEAR.GRADE_5]: [],
    [ACADEMIC_YEAR.GRADE_6]: []
  });
  // いずれかの学年で教科が1つ以上選択されている場合はtrue
  // 1つもチェックが入ってない場合は、errorMessages.subjectFieldRequired のエラーメッセージを表示するのに使う
  const isSubjectFieldChecked = computed<boolean>(() => (
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1].length > 0 ||
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].length > 0 ||
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length > 0 ||
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4].length > 0 ||
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5].length > 0 ||
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6].length > 0
  ));
  // 各学年の教科のチェックボックスが全てチェックされているかどうか
  const isAllSubjectFieldsChecked = computed(() => {
    const isSameElements = (a: number[], b: number[]): boolean => a.length === b.length && a.every(v => b.includes(v));
    return {
      juniorHigh: {
        workOrCbt: {
          grade1: isSameElements(subjectFieldsVals.juniorHigh.workOrCbt.grade1To2, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1]),
          grade2: isSameElements(subjectFieldsVals.juniorHigh.workOrCbt.grade1To2, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2]),
          grade3: isSameElements(subjectFieldsVals.juniorHigh.workOrCbt.grade3, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]),
        },
        nyushi: {
          grade2: isSameElements(subjectFieldsVals.juniorHigh.nyushi.grade2To3, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2]),
          grade3: isSameElements(subjectFieldsVals.juniorHigh.nyushi.grade2To3, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]),
        }
      },
      elementary: {
        workOrCbt: {
          grade3: isSameElements(subjectFieldsVals.elementary.workOrCbt.grade3, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]),
          grade4: isSameElements(subjectFieldsVals.elementary.workOrCbt.grade4To6, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4]),
          grade5: isSameElements(subjectFieldsVals.elementary.workOrCbt.grade4To6, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5]),
          grade6: isSameElements(subjectFieldsVals.elementary.workOrCbt.grade4To6, checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6]),
        }
      }
    }
  });

  // 教科書会社セレクトボックスの selected 用
  // 各学年の教科ごとに、選択された教科書会社コードを保持
  const selectedTextbookPublisher: Record<string, any> = reactive({
    juniorHigh: {
      workOrCbt: {
        grade1: {kokugo: "", chiri: "", rekishi: "", sugaku: "", rika: "", eigo: ""},
        grade2: {kokugo: "", chiri: "", rekishi: "", sugaku: "", rika: "", eigo: ""},
        grade3: {kokugo: "", komin: "", sugaku: "", rika: "", eigo: ""}
      },
      nyushi: {
        grade2: {kokugo: "", shakai: "", sugaku: "", rika: "", eigo: ""},
        grade3: {kokugo: "", shakai: "", sugaku: "", rika: "", eigo: ""}
      }
    },
    elementary: {
      workOrCbt: {
        grade3: {rika: ""},
        grade4: {shakai: "", rika: ""},
        grade5: {shakai: "", rika: ""},
        grade6: {shakai: "", rika: ""},
      },
    },
  });
  // 「標準」の教科書会社コード
  // 入試対策版のときの教科書会社コードの固定値として扱う
  const PUBLISHER_HYOJUNN = 7;

  // エラーメッセージ用
  const errorMessages = {
    // FIXME: 教材の対象年度 のバリデーションを追加する。
    required: "を入力してください。",
    invalid: "の形式が不正です。",
    tooLong: "が長すぎます。",
    phoneInvalid: "電話番号は10桁または11桁の数字で入力してください。",
    schoolInvalid: "の一部を入力、選択肢の中から選んでください。",
    agreeNotChecked: "個人情報の取り扱いについてご確認ください。",
    disclaimerNotChecked: "免責事項についてご確認ください。",
    textbookPublisherRequired: "教科書会社名を選んでください。",
    numOfStudentsRequired: "人数を入力してください。",
    numOfStudentsInvalid: "1〜999の数字で入力ください。",
    subjectFieldRequired: "1つ以上の教材を選択してください。",
    subjectFieldRequiredForTextbookPublisher: "（教材を選ぶと、教科書会社名リストが表示されます。）",
    subjectFieldRequiredForStudent: "（教材を選ぶと、人数入力フォームが表示されます。）",
    schoolNotFound: "学校名の一部を入力、選択肢の中から選んでください。",
    schoolDoesNotMatch: "このメールアドレスは他校で使われています。他校での先生登録を削除してください。",
    schoolAlreadyInUse: "すでにマナビリアをご利用中です。",
  };

  // 各学年の生徒数
  const numOfStudents: Record<number, number | null> = reactive({
    [ACADEMIC_YEAR.GRADE_1]: null,
    [ACADEMIC_YEAR.GRADE_2]: null,
    [ACADEMIC_YEAR.GRADE_3]: null,
    [ACADEMIC_YEAR.GRADE_4]: null,
    [ACADEMIC_YEAR.GRADE_5]: null,
    [ACADEMIC_YEAR.GRADE_6]: null
  });
  const defaultLicenseCount = ref<{academic_year: number, value: number | null}[]>([]);

  const schools = ref<Array<IEntrySchool>>([]);

  /* 教科書会社一覧APIのレスポンスを整形
  *
  * - 「教材の種類」の選択値に応じて、ワーク/CBTの教材を持つ教科書会社に絞り込み
  * - ISelectSingleOptionの配列に変換（{ value: 教科書会社コード, name: 教科書会社名 } の配列）
  * */
  const filteredTextbookPublisherOptions = computed(() =>
    textbookPublishersResultList.value.map(result => ({
      subjectFieldId: result.subject_field_id,
      academicYear: result.academic_year,
      textbookPublishers: !selectedLicenseType.value
        ? []
        : result.textbook_publishers
          .filter(publisher => {
            // 「入試対策版」のときは「教科書会社」選択欄は表示しないため、ここでは「ワーク」「CBT」のみを考慮すればよい
            if (selectedLicenseType.value!.value === LICENSE_TYPE.WORK) return publisher.has_work;
            if (selectedLicenseType.value!.value === LICENSE_TYPE.CBT) return publisher.has_cbt;
            return false;
          })
          .map(publisher => ({ value: publisher.code, name: publisher.name } as ISelectSingleOption)),
    }))
  );
  function getTextbookPublisherOptions(subjectFieldId: number, academicYear: number) {
    const options = filteredTextbookPublisherOptions.value.find(textbook => textbook.subjectFieldId === subjectFieldId && textbook.academicYear === academicYear);
    return options ? options.textbookPublishers : [];
  }

  onMounted(async() => {
    latestAcademicSessionResult.value = await entryStore.fetchLatestAcademicSessions();
    textbookPublishersResultList.value = await entryStore.fetchTextbookPublishers();
    // NFR-7104: show subject for login teacher of elementary school
    if(props.teacherInfo.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL) {
      selectedLicenseType.value = licenseTypeOptions[0];
      getDefaultLicenseCount();
    }
  })

  const setNumOfStudents = (academicYear: number) => {
    if (checkedSubjectFields.value[academicYear].length === 0) {
      numOfStudents[academicYear] = null;
      return;
    }

    const licenseCount = defaultLicenseCount.value.find(
      item => item.academic_year === academicYear
    );

    if (licenseCount && licenseCount.value != null && numOfStudents[academicYear] == null) {
      numOfStudents[academicYear] = licenseCount.value;
    }
  };

  async function getDefaultLicenseCount() {
    if(!form.schoolCode || !selectedLicenseType.value || !form.schoolTypeCode) return;
    defaultLicenseCount.value = await entryStore.fetchDefaultLicenseCount(form.schoolCode, selectedLicenseType.value.value, form.schoolTypeCode);
  }

  const form: IEntry = reactive({
    schoolName: props.teacherInfo.schoolName,
    schoolCode: props.teacherInfo.schoolCode,
    phone: props.teacherInfo.schoolPhone,
    contactPhone: "",
    applicantName: props.teacherInfo.applicantName,
    email: props.teacherInfo.email,
    schoolTypeCode: props.teacherInfo.schoolTypeCode,
  });
  const personalInfoConsent: IPersonalInfoConsent = reactive({
    agree: false,
    disclaimer: false,
  });
  const error: Record<string, string> = reactive({
    schoolName: "",
    phone: "",
    contactPhone: "",
    applicantName: "",
    email: "",
    numOfStudentsGrade1: "",
    numOfStudentsGrade2: "",
    numOfStudentsGrade3: "",
    numOfStudentsGrade4: "",
    numOfStudentsGrade5: "",
    numOfStudentsGrade6: "",
    agree: "",
    disclaimer: "",
  });
  const title = {
    schoolName: "学校名",
    phone: "学校の代表電話番号",
    contactPhone: "ご連絡先電話番号",
    applicantName: "先生のお名前",
    email: "メールアドレス",
  };
  // テキストのバリデーション
  function validateText(target: string): void {
    const targetForm = form[target];
    const targetTitle = title[target];
    if (!targetForm) {
      error[target] = targetTitle + errorMessages.required;
    } else if (targetForm.length > 255) {
      error[target] = targetTitle + errorMessages.tooLong;
    } else {
      error[target] = "";
    }
  }
  // メールアドレスのバリデーション
  // FIXME: 1行が長くなってしまったので、正規表現のグループごとに改行する等して可読性を上げる
  const emailPattern: RegExp = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]$/;
  function validateEmail(target: string): void {
    form[target] = form[target].replace(/＠/g, "@");
    const targetForm = form[target];
    const targetTitle = title[target];
    if (!targetForm) {
      error[target] = targetTitle + errorMessages.required;
    } else if (targetForm.length > 254 || !emailPattern.test(targetForm)) {
      error[target] = targetTitle + errorMessages.invalid;
    } else {
      error[target] = "";
    }
  }
  // 電話番号のバリデーション
  const telPattern: RegExp = /[0-9０-９]/g;
  const fulPattern: RegExp = /[０-９]/g;
  const fullNumbers = '０１２３４５６７８９';
  function validatePhone(target: string): void {
    // 半角数字と全角数字のみに変換
    form[target] = form[target].match(telPattern)?.join("") ?? "";
    // 全角数字を半角数字に変換
    form[target] = form[target].replace(fulPattern, (s: string) => fullNumbers.indexOf(s).toString());
    const targetForm = form[target];
    const targetTitle = title[target];
    if (!targetForm) {
      error[target] = targetTitle + errorMessages.required;
    } else if (!(targetForm.length === 10 || targetForm.length === 11)) {
      error[target] = errorMessages.phoneInvalid;
    } else {
      error[target] = "";
    }
  }
  // 学校のバリデーション
  function validateSchool(target: string): void {
    const targetForm = form[target];
    const targetTitle = title[target];
    // 学校名が入力されているかつ、datalistの中に存在する学校名が選択されているか
    const inputSchool = schools.value.find(school => school.name + "（" + school.prefecture_name + "）" === form.schoolName);
    form.schoolCode = inputSchool?.code ?? "";
    if (!targetForm || !inputSchool) {
      error[target] = targetTitle + errorMessages.schoolInvalid;
    } else {
      error[target] = "";
    }
    getDefaultLicenseCount();
  }
  // 個人情報の取り扱いに同意しているかのバリデーション
  function validateAgree(): void {
    if (!personalInfoConsent.agree) {
      error.agree = errorMessages.agreeNotChecked;
    } else {
      error.agree = "";
    }
  }
  // NFR-6951 免責事項に同意しているかのバリデーション
  function validateDisclaimer(): void {
    if (!personalInfoConsent.disclaimer) {
      error.disclaimer = errorMessages.disclaimerNotChecked;
    } else {
      error.disclaimer = "";
    }
  }
  const step = ref<number>(1);
  // 未入力項目があるまたはエラーがある場合は次へボタンを無効化
  // ログイン先生は学校代表電話番号入力欄が描画されず、学校マスタに電話番号が
  // 登録されていない場合は form.phone を入力する手段がないため、contactPhone のみを必須チェックする
  const isNextButtonDisabled = computed<boolean>(() => {
    if (props.isTeacher) {
      return !form.contactPhone || !!error.contactPhone;
    }
    return Object.values(form).some(value => !value) || Object.values(error).some(value => value)
  });
  // 次へボタンが押されたらステップ2へ進め、次へは非表示にする
  const isStep1Submitted = ref<boolean>(false);
  function nextStep(): void {
    step.value = 2;
    isStep1Submitted.value = true;
    nextTick(() => {
      location.href = "#step2";
    });
  }
  // NFR-6643: schoolTypeCodeの選択肢を変更したときの処理
  async function changeSchoolTypeCode(school_type_code: IEntryFormSelectSingleOption) {
    schools.value = await entryStore.fetchSchools(school_type_code.value);
    form.schoolTypeCode = school_type_code.value;
    if(form.schoolName){
      form.schoolName = "";
      // 学校が変わったら学校のバリデーションを行う
      validateSchool("schoolName");
    }
    if(school_type_code.value === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL){
      changeLicenseType(licenseTypeOptions[0]);
    } else {
      changeLicenseType(null);
    }
  }
  // NFR-6629: reset textbook publisher and grade when license type is changed
  function fillSelectedTextbookPublisher(reactiveObj: any, defaultValue: string | number) {
    Object.keys(reactiveObj).forEach(key => {
      reactiveObj[key] = defaultValue;
    });
  }
  // NFR-6629: processing when license type is changed
  function changeLicenseType(licenseType: IEntryFormSelectSingleOption | null) {
    selectedLicenseType.value = licenseType;
    // reset subject fields, textbook_publisher_grade and number of students
    hasInteractedSubjectField.value = false;
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1] = [];
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2] = [];
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3] = [];
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4] = [];
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5] = [];
    checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6] = [];
    fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade3, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade4, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade5, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade6, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade1, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade2, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade3, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.nyushi.grade2, "");
    fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.nyushi.grade3, "");
    Object.keys(numOfStudents).forEach(key => {
      numOfStudents[Number(key)] = null;
    });
    getDefaultLicenseCount();
  }

  // 各学年の「全教科」のチェックをON/OFFしたとき
  function changeAllSubjectFields(academicYear: number) {
    if(form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL){
      if (academicYear === ACADEMIC_YEAR.GRADE_3) {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3] = (
          isAllSubjectFieldsChecked.value.elementary.workOrCbt.grade3 ? [] : subjectFieldsVals.elementary.workOrCbt.grade3
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade3, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      } else if (academicYear === ACADEMIC_YEAR.GRADE_4) {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4] = (
          isAllSubjectFieldsChecked.value.elementary.workOrCbt.grade4 ? [] : subjectFieldsVals.elementary.workOrCbt.grade4To6
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade4, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_4);
      } else if (academicYear === ACADEMIC_YEAR.GRADE_5) {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5] = (
          isAllSubjectFieldsChecked.value.elementary.workOrCbt.grade5 ? [] : subjectFieldsVals.elementary.workOrCbt.grade4To6
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade5, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_5);
      } else {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6] = (
          isAllSubjectFieldsChecked.value.elementary.workOrCbt.grade6 ? [] : subjectFieldsVals.elementary.workOrCbt.grade4To6
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.elementary.workOrCbt.grade6, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_6);
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.WORK || selectedLicenseType.value?.value === LICENSE_TYPE.CBT) {
      // Junior high school (WORK or CBT)
      if (academicYear === ACADEMIC_YEAR.GRADE_1) {
        // 元々各教科にチェックされていたらすべてチェックを外し、そうでなければすべてチェックを入れる
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1] = (
          isAllSubjectFieldsChecked.value.juniorHigh.workOrCbt.grade1 ? [] : subjectFieldsVals.juniorHigh.workOrCbt.grade1To2
        );
        // チェックを外した場合は、教科書会社の選択をクリア
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade1, "");
        }
        // 生徒数の入力欄は、チェックを外した場合はクリア。チェックが付いたかつ未入力の場合はデフォルト値をセット
        setNumOfStudents(ACADEMIC_YEAR.GRADE_1);
      } else if (academicYear === ACADEMIC_YEAR.GRADE_2) {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2] = (
          isAllSubjectFieldsChecked.value.juniorHigh.workOrCbt.grade2 ? [] : subjectFieldsVals.juniorHigh.workOrCbt.grade1To2
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade2, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_2);
      } else {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3] = (
          isAllSubjectFieldsChecked.value.juniorHigh.workOrCbt.grade3 ? [] : subjectFieldsVals.juniorHigh.workOrCbt.grade3
        );
        if (!checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length) {
          fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.workOrCbt.grade3, "");
        }
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.NYUSHI) {
      // Junior high school (NYUSHI)
      if (academicYear === ACADEMIC_YEAR.GRADE_2) {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2] = (
          isAllSubjectFieldsChecked.value.juniorHigh.nyushi.grade2 ? [] : subjectFieldsVals.juniorHigh.nyushi.grade2To3
        );
        // 入試対策版のときは、「教科」が選択されたときは「教科書会社」選択欄は表示せずに「標準」(=7)が選択されているものとして扱う
        const defaultValue = !checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].length ? "" : PUBLISHER_HYOJUNN;
        fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.nyushi.grade2, defaultValue);
        setNumOfStudents(ACADEMIC_YEAR.GRADE_2);
      } else {
        checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3] = (
          isAllSubjectFieldsChecked.value.juniorHigh.nyushi.grade3 ? [] : subjectFieldsVals.juniorHigh.nyushi.grade2To3
        );
        const defaultValue = !checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length ? "" : PUBLISHER_HYOJUNN;
        fillSelectedTextbookPublisher(selectedTextbookPublisher.juniorHigh.nyushi.grade3, defaultValue);
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      }
    }
    hasInteractedSubjectField.value = true;
  }

  // 各学年の教科のチェックを付け外したとき
  function changeSubjectField(academicYear: number, subjectField: any) {
    if(form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL){
      if (academicYear === ACADEMIC_YEAR.GRADE_3) {
        selectedTextbookPublisher.elementary.workOrCbt.grade3[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      }
      else if (academicYear === ACADEMIC_YEAR.GRADE_4) {
        selectedTextbookPublisher.elementary.workOrCbt.grade4[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_4);
      } else if (academicYear === ACADEMIC_YEAR.GRADE_5) {
        selectedTextbookPublisher.elementary.workOrCbt.grade5[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_5);
      } else {
        selectedTextbookPublisher.elementary.workOrCbt.grade6[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_6);
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.WORK || selectedLicenseType.value?.value === LICENSE_TYPE.CBT) {
      // Junior high school (WORK or CBT)
      if (academicYear === ACADEMIC_YEAR.GRADE_1) {
        // 教科書会社の選択をクリア
        selectedTextbookPublisher.juniorHigh.workOrCbt.grade1[subjectField.name] = "";
        // 生徒数の入力欄は、チェックを外した場合はクリア。チェックが付いたかつ未入力の場合はデフォルト値をセット
        setNumOfStudents(ACADEMIC_YEAR.GRADE_1);
      } else if (academicYear === ACADEMIC_YEAR.GRADE_2) {
        selectedTextbookPublisher.juniorHigh.workOrCbt.grade2[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_2);
      } else {
        selectedTextbookPublisher.juniorHigh.workOrCbt.grade3[subjectField.name] = "";
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.NYUSHI) {
      // Junior high school (NYUSHI)
      if (academicYear === ACADEMIC_YEAR.GRADE_2) {
        // 入試対策版のときは、「教科」が選択されたときは「教科書会社」選択欄は表示せずに「標準」(=7)が選択されているものとして扱う
        selectedTextbookPublisher.juniorHigh.nyushi.grade2[subjectField.name] = (
          checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].includes(subjectField.val) ? PUBLISHER_HYOJUNN : ""
        );
        setNumOfStudents(ACADEMIC_YEAR.GRADE_2);
      } else {
        selectedTextbookPublisher.juniorHigh.nyushi.grade3[subjectField.name] = (
          checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].includes(subjectField.val) ? PUBLISHER_HYOJUNN : ""
        );
        setNumOfStudents(ACADEMIC_YEAR.GRADE_3);
      }
    }
    hasInteractedSubjectField.value = true;
  }

  // 各学年で、教科分野が1つも選択されていない場合はtrue
  const isSubjectFieldNotSelected = computed<Record<number, boolean>>(() => {
    return {
      [ACADEMIC_YEAR.GRADE_1]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1].length === 0,
      [ACADEMIC_YEAR.GRADE_2]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].length === 0,
      [ACADEMIC_YEAR.GRADE_3]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length === 0,
      [ACADEMIC_YEAR.GRADE_4]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4].length === 0,
      [ACADEMIC_YEAR.GRADE_5]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5].length === 0,
      [ACADEMIC_YEAR.GRADE_6]: checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6].length === 0,
    };
  });

  // 教科分野が選択されている学年で、教科書会社が選択されていない学年・教科分野がある場合はtrue
  const isTextbookPublisherNotFilled = computed<boolean>(() => {
    if (form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL) {
      return (
        !subjectFields.elementary.workOrCbt.grade3
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].includes(sf.val))
          .every(sf => selectedTextbookPublisher.elementary.workOrCbt.grade3[sf.name] !== "") ||
        !subjectFields.elementary.workOrCbt.grade4To6
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4].includes(sf.val))
          .every(sf => selectedTextbookPublisher.elementary.workOrCbt.grade4[sf.name] !== "") ||
        !subjectFields.elementary.workOrCbt.grade4To6
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5].includes(sf.val))
          .every(sf => selectedTextbookPublisher.elementary.workOrCbt.grade5[sf.name] !== "") ||
        !subjectFields.elementary.workOrCbt.grade4To6
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6].includes(sf.val))
          .every(sf => selectedTextbookPublisher.elementary.workOrCbt.grade6[sf.name] !== "")
      );
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.WORK || selectedLicenseType.value?.value === LICENSE_TYPE.CBT) {
      // Junior high school (WORK or CBT)
      return (
        !subjectFields.juniorHigh.workOrCbt.grade1To2
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1].includes(sf.val))
          .every(sf => selectedTextbookPublisher.juniorHigh.workOrCbt.grade1[sf.name] !== "") ||
        !subjectFields.juniorHigh.workOrCbt.grade1To2
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].includes(sf.val))
          .every(sf => selectedTextbookPublisher.juniorHigh.workOrCbt.grade2[sf.name] !== "") ||
        !subjectFields.juniorHigh.workOrCbt.grade3
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].includes(sf.val))
          .every(sf => selectedTextbookPublisher.juniorHigh.workOrCbt.grade3[sf.name] !== "")
      );
    } else {
      // Junior high school (NYUSHI)
      return (
        !subjectFields.juniorHigh.nyushi.grade2To3
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].includes(sf.val))
          .every(sf => selectedTextbookPublisher.juniorHigh.nyushi.grade2[sf.name] !== "") ||
        !subjectFields.juniorHigh.nyushi.grade2To3
          .filter(sf => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].includes(sf.val))
          .every(sf => selectedTextbookPublisher.juniorHigh.nyushi.grade3[sf.name] !== "")
      );
    }
  });

  // 教科分野が選択されている学年で、生徒数が入力されていない学年がある場合はtrue
  const isNumOfStudentsNotFilled = computed<boolean>(() => {
    return (
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_1] === null ||
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_2] === null ||
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_3] === null ||
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_4] === null ||
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_5] === null ||
      checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6].length > 0 && numOfStudents[ACADEMIC_YEAR.GRADE_6] === null
    );
  });

  // 未入力項目があるまたはエラーがある場合は申し込むボタンを無効化
  const isEntryButtonDisabled = computed<boolean>(() => {
    // STEP1 の必須項目が未入力の場合true (エラー項目は後述の isStep2Invalid 側でまとめてチェック)
    // ログイン先生は学校代表電話番号入力欄が描画されず、学校マスタに電話番号が
    // 登録されていない場合は form.phone を入力する手段がないため、contactPhone のみを必須チェックする
    const isStep1Invalid = props.isTeacher
      ? !form.contactPhone
      : Object.values(form).some(value => !value);

    // STEP2でバリデーションエラーの項目がある場合true
    const isStep2Invalid = (
      Object.values(error).some(value => value) ||
      !selectedLicenseType.value ||
      !isSubjectFieldChecked.value ||
      isTextbookPublisherNotFilled.value ||
      isNumOfStudentsNotFilled.value ||
      !personalInfoConsent.agree ||
      !personalInfoConsent.disclaimer
    );

    return isStep1Invalid || isStep2Invalid;
  });

  // 生徒数のバリデーション
  function validateNumOfStudents(academicYear: number) {
    if (academicYear === ACADEMIC_YEAR.GRADE_1) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_1]) {
        error.numOfStudentsGrade1 = "";
        return;
      }
      const grade1Number = numOfStudents[ACADEMIC_YEAR.GRADE_1];
      if (!grade1Number) {
        error.numOfStudentsGrade1 = errorMessages.numOfStudentsRequired;
      } else if (grade1Number < 1 || 999 < grade1Number) {
        error.numOfStudentsGrade1 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade1 = "";
      }
    } else if (academicYear === ACADEMIC_YEAR.GRADE_2) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_2]) {
        error.numOfStudentsGrade2 = "";
        return;
      }
      const grade2Number = numOfStudents[ACADEMIC_YEAR.GRADE_2];
      if (!grade2Number) {
        error.numOfStudentsGrade2 = errorMessages.numOfStudentsRequired;
      } else if (grade2Number < 1 || 999 < grade2Number) {
        error.numOfStudentsGrade2 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade2 = "";
      }
    } else if (academicYear === ACADEMIC_YEAR.GRADE_3) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_3]) {
        error.numOfStudentsGrade3 = "";
        return;
      }
      const grade3Number = numOfStudents[ACADEMIC_YEAR.GRADE_3];
      if (!grade3Number) {
        error.numOfStudentsGrade3 = errorMessages.numOfStudentsRequired;
      } else if (grade3Number < 1 || 999 < grade3Number) {
        error.numOfStudentsGrade3 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade3 = "";
      }
    } else if (academicYear === ACADEMIC_YEAR.GRADE_4) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_4]) {
        error.numOfStudentsGrade4 = "";
        return;
      }
      const grade4Number = numOfStudents[ACADEMIC_YEAR.GRADE_4];
      if (!grade4Number) {
        error.numOfStudentsGrade4 = errorMessages.numOfStudentsRequired;
      } else if (grade4Number < 1 || 999 < grade4Number) {
        error.numOfStudentsGrade4 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade4 = "";
      }
    } else if (academicYear === ACADEMIC_YEAR.GRADE_5) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_5]) {
        error.numOfStudentsGrade5 = "";
        return;
      }
      const grade5Number = numOfStudents[ACADEMIC_YEAR.GRADE_5];
      if (!grade5Number) {
        error.numOfStudentsGrade5 = errorMessages.numOfStudentsRequired;
      } else if (grade5Number < 1 || 999 < grade5Number) {
        error.numOfStudentsGrade5 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade5 = "";
      }
    } else if(academicYear === ACADEMIC_YEAR.GRADE_6) {
      if (isSubjectFieldNotSelected.value[ACADEMIC_YEAR.GRADE_6]) {
        error.numOfStudentsGrade6 = "";
        return;
      }
      const grade6Number = numOfStudents[ACADEMIC_YEAR.GRADE_6];
      if (!grade6Number) {
        error.numOfStudentsGrade6 = errorMessages.numOfStudentsRequired;
      } else if (grade6Number < 1 || 999 < grade6Number) {
        error.numOfStudentsGrade6 = errorMessages.numOfStudentsInvalid;
      } else {
        error.numOfStudentsGrade6 = "";
      }
    }
  };

  // 教科分野の選択を監視して、各学年の教科分野の選択がなくなったら、生徒数のエラーもなくす
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade1 = "";
    }
  );
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade2 = "";
    }
  );
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade3 = "";
    }
  );
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade4 = "";
    }
  );
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade5 = "";
    }
  );
  watch(
    () => checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6],
    (newVal, _) => {
      if (newVal.length === 0) error.numOfStudentsGrade6 = "";
    }
  );

  function setFormErrorMessage(teacherError: ITeacherError): void {
    formError.value = teacherError;
    switch (teacherError.code) {
      case "E02":
        error.schoolName = errorMessages.schoolNotFound;
        break;
      case "E03":
        error.email = errorMessages.schoolDoesNotMatch;
        break;
      case "E04":
        error.schoolName = errorMessages.schoolAlreadyInUse;
        break;
      default:
        break;
    };
  };

  async function addEntry() {
    // 利用申込する教材情報をworksに格納
    const works = [];
    if(form.schoolTypeCode === SCHOOL_TYPE_CODE.ELEMENTARY_SCHOOL){
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_3,
          textbook_publisher: selectedTextbookPublisher.elementary.workOrCbt.grade3[
            subjectFields.elementary.workOrCbt.grade3.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_4]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_4,
          textbook_publisher: selectedTextbookPublisher.elementary.workOrCbt.grade4[
            subjectFields.elementary.workOrCbt.grade4To6.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_5]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_5,
          textbook_publisher: selectedTextbookPublisher.elementary.workOrCbt.grade5[
            subjectFields.elementary.workOrCbt.grade4To6.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_6]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_6,
          textbook_publisher: selectedTextbookPublisher.elementary.workOrCbt.grade6[
            subjectFields.elementary.workOrCbt.grade4To6.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.WORK || selectedLicenseType.value?.value === LICENSE_TYPE.CBT) {
      // Junior high school (WORK or CBT)
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_1]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_1,
          textbook_publisher: selectedTextbookPublisher.juniorHigh.workOrCbt.grade1[
            subjectFields.juniorHigh.workOrCbt.grade1To2.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_2,
          textbook_publisher: selectedTextbookPublisher.juniorHigh.workOrCbt.grade2[
            subjectFields.juniorHigh.workOrCbt.grade1To2.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_3,
          textbook_publisher: selectedTextbookPublisher.juniorHigh.workOrCbt.grade3[
            subjectFields.juniorHigh.workOrCbt.grade3.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
    } else if (selectedLicenseType.value?.value === LICENSE_TYPE.NYUSHI) {
      // Junior high school (NYUSHI)
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_2]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_2,
          textbook_publisher: selectedTextbookPublisher.juniorHigh.nyushi.grade2[
            subjectFields.juniorHigh.nyushi.grade2To3.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
      for (const subjectField of checkedSubjectFields.value[ACADEMIC_YEAR.GRADE_3]) {
        works.push({
          subject_field: subjectField,
          academic_year: ACADEMIC_YEAR.GRADE_3,
          textbook_publisher: selectedTextbookPublisher.juniorHigh.nyushi.grade3[
            subjectFields.juniorHigh.nyushi.grade2To3.find(sf => sf.val === subjectField)!.name
          ],
        });
      }
    }

    // 利用申込対象外の学年の生徒数はnullか空文字となり、serializerで400エラーとなるため、0に変換
    Object.keys(numOfStudents).forEach(key => {
      numOfStudents[Number(key)] = numOfStudents[Number(key)] ?? 0;
    });
    entryAddResult.value = await entryStore.fetchEntryAdd(
      {
        school_code: form.schoolCode,
        school_name: form.schoolName,
        phone: form.phone,
        contact_phone: form.contactPhone,
        applicant_name: form.applicantName,
        email: form.email,
        academic_session_id: latestAcademicSessionResult.value[0].id,
        number_of_grade1_students: numOfStudents[ACADEMIC_YEAR.GRADE_1],
        number_of_grade2_students: numOfStudents[ACADEMIC_YEAR.GRADE_2],
        number_of_grade3_students: numOfStudents[ACADEMIC_YEAR.GRADE_3],
        number_of_grade4_students: numOfStudents[ACADEMIC_YEAR.GRADE_4],
        number_of_grade5_students: numOfStudents[ACADEMIC_YEAR.GRADE_5],
        number_of_grade6_students: numOfStudents[ACADEMIC_YEAR.GRADE_6],
        license_type: selectedLicenseType.value!.value,
        works: works,
        school_type_code: form.schoolTypeCode,
      }
    );
    if (entryAddResult.value) {
      setFormErrorMessage(entryAddResult.value);
      nextTick(() => {
        location.href = "#error";
      });
    }
  }
</script>
